export function useCrossAppGuard() {
  const {
    $i18n: { t },
  } = useNuxtApp();
  const { isMultiTenant } = useIsMultiTenant();
  const { authStore, currentUser, currentOrganization } = useOrganizationApi();
  const { createCrossAppRedirectPath } = useAuthQueryParams();
  const { resolveRouteContext, makeGuardResult } = useRouteGuard();

  const guardKleanzAccount = (payload: {
    organizationExistsInKleanz: boolean;
    userExistsInKleanz: boolean;
    canEnableApps: boolean;
    sourceAppName?: string;
    authFlow?: AuthFlow;
  }) => {
    // For now the fallback is fixed to "sureTrend" since is the only external app we use
    const {
      organizationExistsInKleanz,
      userExistsInKleanz,
      canEnableApps,
      sourceAppName = "sureTrend",
    } = payload;

    if (organizationExistsInKleanz && userExistsInKleanz) {
      return;
    }

    // Only redirect after user manually logs in
    if (!organizationExistsInKleanz && payload.authFlow !== "post-login") {
      if (canEnableApps) {
        devConsole.info(
          " 🔀  Cross App Redirect: scenarios E1 logged in/redeem account, NO Org and CanEnableApps = true -> show welcome page",
          payload,
        );

        return "welcome";
      }

      devConsole.warn(
        " 🔀  Cross App Redirect: scenarios E2 logged in/redeem account, NO Org and CanEnableApps = FALSE -> error page with ask administrator message",
        payload,
      );
      throw new $ck.utils.PageFriendlyError({
        heading: "401",
        subHeading: "auth.askAdminForAccess",
        title: "auth.askAdminForAccess",
        message: "auth.askAdminForAccessMessage",
        actionText: t("auth.goBackTo", { name: sourceAppName }),
        redirect: () =>
          createCrossAppRedirectPath(sourceAppName, {
            error: "auth.redirectReasonNoKLEANZOrganization",
          }),
      });
    }

    if (canEnableApps) {
      // TODO (JM): logged in case is pending
      devConsole.info(
        " 🔀  Cross App Redirect: scenarios E3 redeem account WITH Org NO user and CanEnableApps = true -> redirect to jetsetter -> should come back to self-service so to fall in E1, E2, or E4",
        payload,
      );

      return createCrossAppRedirectPath("sureKleanz", {
        error: "auth.redirectReasonNoKLEANZUser",
      });
    }

    devConsole.warn(
      " 🔀  Cross App Redirect: scenarios E4 logged in/redeem account WITH Org and CanEnableApps = FALSE -> error page with unauthorized access",
      payload,
    );
    throw new $ck.utils.PageFriendlyError({
      title: "auth.unauthorizedAccess",
      heading: "401",
      subHeading: "auth.unauthorizedAccess",
      message: "auth.signupPermissionRejectedMessage",
      actionText: t("auth.goBackTo", { name: sourceAppName }),
      redirect: () =>
        createCrossAppRedirectPath(sourceAppName, {
          error: "auth.redirectReasonNoKLEANZUser",
        }),
    });
  };

  /**
   * @return GuardResult
   */
  const guardRouteAccess: AccessGuard = async ({ to, authFlow }) => {
    const routeContext = resolveRouteContext(to);
    if (
      !authStore.isAuthed ||
      !isMultiTenant.value ||
      routeContext.isRedirectingPage
    ) {
      return makeGuardResult(to, "continue");
    }

    const redirectResult = guardKleanzAccount({
      organizationExistsInKleanz: !!currentOrganization.value,
      userExistsInKleanz: !!currentUser.value,
      canEnableApps: authStore.currentAccount?.userCanEnableApps ?? false,
      sourceAppName: routeContext.query.from?.toString(),
      authFlow,
    });

    if (typeof redirectResult === "string") {
      // TODO (JM): handle login case
      if (redirectResult === "welcome") {
        return makeGuardResult(to, "continue");
      }
      return makeGuardResult(to, {
        redirect: redirectResult,
      });
    }

    return makeGuardResult(to, "continue");
  };

  return {
    name: "crossAppGuard",
    guardRouteAccess,
    guardKleanzAccount,
  };
}
